import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Building2, Save } from 'lucide-react';
import { apiGet, apiPatch, ApiError } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

interface Org {
  id: string; slug: string; legal_name: string; trade_name?: string;
  cnpj?: string; timezone?: string; cme_class?: string;
  rt_name?: string; rt_coren?: string; anvisa_license?: string;
  license_issued_at?: string; license_expires_at?: string;
  erp_webhook_url?: string; plan?: string;
}
interface Stats {
  users: { n: number }; items: { n: number }; kits: { n: number };
  batches: Array<{ status: string; n: number }>;
  flow_24h: Array<{ phase: string; n: number }>;
}

const STATUS_TONE: Record<string, any> = {
  PREPARING: 'neutral', RUNNING: 'info', PENDING_PARAM_RELEASE: 'warning',
  PENDING_BI: 'warning', APPROVED: 'success', RELEASED: 'success',
  REJECTED: 'danger', RECALLED: 'danger',
};

export function OrganizacaoPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole('ADMIN');
  const qc = useQueryClient();
  const org = useQuery({ queryKey: ['org'], queryFn: () => apiGet<Org>('/v1/orgs') });
  const stats = useQuery({ queryKey: ['org-stats'], queryFn: () => apiGet<Stats>('/v1/orgs/stats'), refetchInterval: 60_000 });

  const [form, setForm] = useState<Partial<Org>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (org.data) setForm(org.data); }, [org.data]);

  const save = useMutation({
    mutationFn: (body: any) => apiPatch('/v1/orgs', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['org'] }); setError(null); },
    onError: (e) => setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)),
  });

  function submit() {
    const body: any = {};
    for (const k of ['legal_name','trade_name','cnpj','timezone','cme_class','rt_name','rt_coren','anvisa_license','license_issued_at','license_expires_at','erp_webhook_url'] as const) {
      const v = (form as any)[k];
      if (v !== undefined && v !== '') body[k] = v;
    }
    save.mutate(body);
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader title="Organização" subtitle={org.data?.slug ? `slug: ${org.data.slug}` : ''} action={<Building2 className="h-5 w-5 text-brand-500" />} />
        <CardBody>
          {org.isLoading ? (
            <p className="text-sm text-neutral-500">Carregando...</p>
          ) : (
            <form className="grid grid-cols-1 gap-3 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); submit(); }}>
              <Input label="Razão social" value={form.legal_name ?? ''} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} disabled={!canEdit} />
              <Input label="Nome fantasia" value={form.trade_name ?? ''} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} disabled={!canEdit} />
              <Input label="CNPJ" value={form.cnpj ?? ''} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} disabled={!canEdit} placeholder="00.000.000/0000-00" />
              <Input label="Fuso horário (IANA)" value={form.timezone ?? ''} onChange={(e) => setForm({ ...form, timezone: e.target.value })} disabled={!canEdit} placeholder="America/Sao_Paulo" />
              <Input label="Classe da CME" value={form.cme_class ?? ''} onChange={(e) => setForm({ ...form, cme_class: e.target.value })} disabled={!canEdit} hint="I, II ou III (RDC 15)" />
              <Input label="Plano" value={form.plan ?? ''} onChange={(e) => setForm({ ...form, plan: e.target.value })} disabled />
              <div className="sm:col-span-2 border-t border-neutral-200 pt-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:border-neutral-700">Responsável Técnico (RT)</div>
              <Input label="Nome do RT" value={form.rt_name ?? ''} onChange={(e) => setForm({ ...form, rt_name: e.target.value })} disabled={!canEdit} />
              <Input label="COREN" value={form.rt_coren ?? ''} onChange={(e) => setForm({ ...form, rt_coren: e.target.value })} disabled={!canEdit} />
              <div className="sm:col-span-2 border-t border-neutral-200 pt-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:border-neutral-700">ANVISA</div>
              <Input label="Licença sanitária" value={form.anvisa_license ?? ''} onChange={(e) => setForm({ ...form, anvisa_license: e.target.value })} disabled={!canEdit} />
              <div />
              <Input label="Emitida em" type="date" value={form.license_issued_at?.slice(0, 10) ?? ''} onChange={(e) => setForm({ ...form, license_issued_at: e.target.value })} disabled={!canEdit} />
              <Input label="Vence em" type="date" value={form.license_expires_at?.slice(0, 10) ?? ''} onChange={(e) => setForm({ ...form, license_expires_at: e.target.value })} disabled={!canEdit} />
              <div className="sm:col-span-2 border-t border-neutral-200 pt-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:border-neutral-700">Integração ERP</div>
              <div className="sm:col-span-2">
                <Input label="Webhook ERP (URL)" value={form.erp_webhook_url ?? ''} onChange={(e) => setForm({ ...form, erp_webhook_url: e.target.value })} disabled={!canEdit} placeholder="https://erp.hospital.com.br/ictus/webhook" hint="Eventos OPME, recall e liberação são entregues aqui assinados com HMAC-SHA256." />
              </div>
              {canEdit ? (
                <div className="sm:col-span-2 flex items-center gap-3">
                  <Button leadingIcon={<Save className="h-4 w-4" />} loading={save.isPending}>Salvar</Button>
                  {error ? <p className="text-sm text-danger">{error}</p> : null}
                  {save.isSuccess ? <p className="text-sm text-success">Salvo.</p> : null}
                </div>
              ) : null}
            </form>
          )}
        </CardBody>
      </Card>

      <div className="space-y-4">
        <Card>
          <CardHeader title="Indicadores" />
          <CardBody className="space-y-3">
            <Stat label="Usuários ativos" value={stats.data?.users.n ?? '—'} />
            <Stat label="Itens em inventário" value={stats.data?.items.n ?? '—'} />
            <Stat label="Kits instanciados" value={stats.data?.kits.n ?? '—'} />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Lotes por status" />
          <CardBody>
            {(stats.data?.batches ?? []).length === 0 ? (
              <p className="text-xs text-neutral-500">Sem lotes ainda.</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {stats.data!.batches.map((b) => (
                  <li key={b.status} className="flex items-center justify-between">
                    <Badge tone={STATUS_TONE[b.status] ?? 'neutral'}>{b.status}</Badge>
                    <span className="tabular-nums font-medium">{b.n}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Fluxo 24h (fases)" />
          <CardBody>
            {(stats.data?.flow_24h ?? []).length === 0 ? (
              <p className="text-xs text-neutral-500">Sem eventos nas últimas 24h.</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {stats.data!.flow_24h.map((f) => (
                  <li key={f.phase} className="flex items-center justify-between">
                    <span className="text-neutral-700 dark:text-neutral-300">{f.phase}</span>
                    <span className="tabular-nums font-medium">{f.n}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-neutral-600 dark:text-neutral-400">{label}</span>
      <span className="text-xl font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{value}</span>
    </div>
  );
}
